import IconsEnum from '@enums/icons.enum'
import { MediaPreApproval, Tag, User, defaultImage, onUserChange } from '@hub/shared'
import { SqButton, SqCallout, SqCarousel, SqLoader, SqTip } from '@squidit/react-css'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'

import './media-pre-approval-card.component.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  media?: MediaPreApproval
  editMedia?: (mediaSelected: MediaPreApproval) => Promise<void>
  convertMedia?: (mediaSelected: MediaPreApproval) => Promise<void>
}

export default ({ media, editMedia, convertMedia, className = '', style }: Props) => {
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const iconsEnum = useMemo(() => IconsEnum, [])
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  const [width, setWidth] = useState(window.innerWidth)
  const [loadingConversion, setLoadingConversion] = useState(false)

  const { t, i18n } = useTranslation('mediaPreApproval')

  const conversionStatus = useMemo(() => media?.medias?.[0]?.conversionStatus, [media?.medias])
  const identity = useMemo(() => user?.identities?.find((identity) => identity.id === media?.socialId), [user, media?.socialId])
  const canEdit = useMemo(
    () =>
      ['finalReproved', 'pending', 'inprogress'].includes(media?.status) &&
      !media?.medias?.some((media) => ['toConvert', 'converting'].includes(media?.conversionStatus)),
    [media?.status, media?.medias]
  )

  const factory = {
    status: {
      finalReproved: {
        text: t('finalReproved'),
        icon: 'fa-solid fa-diamond-exclamation',
        color: 'var(--yellow)',
        textTip: t('tooltip.finalReproved'),
      },
      pending: {
        text: t('pending'),
        icon: 'fa-solid fa-circle-check',
        color: 'var(--yellow)',
        textTip: t('tooltip.pending'),
      },
      inprogress: {
        text: t('inprogress'),
        icon: 'fa-solid fa-circle-check',
        color: 'var(--yellow)',
        textTip: t('tooltip.inprogress'),
      },
      finalApproved: {
        text: t('finalApproved'),
        icon: 'fa-solid fa-circle-check',
        color: 'var(--green)',
        textTip: t('tooltip.approved'),
      },
    },
  }

  const onEditMedia = useCallback(() => {
    if (editMedia && typeof editMedia === 'function') {
      editMedia(media)
    }
  }, [editMedia, media])

  useEffect(() => {
    onUserChangeSubscription.current = onUserChange.subscribe((userChanged: User) => {
      setUser(userChanged)
      setLoading(false)
    })

    return () => onUserChangeSubscription.current.unsubscribe()
  }, [])

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const handleSendToConversion = useCallback(async () => {
    setLoadingConversion(true)
    if (convertMedia && typeof convertMedia === 'function') {
      try {
        await convertMedia(media)
      } catch (error) {
        setLoadingConversion(false)
      }
    }
  }, [convertMedia, media])

  const tagConversionError = (
    <SqCallout className="display-flex align-items-center box error" backgroundColor="var('--color_bg_box_system_danger')">
      <i className="fa-regular fa-times red mr-3" style={{ alignContent: 'center' }} />
      <span className="text-bold small">{t('messageConversionError')}</span>
    </SqCallout>
  )

  const videoIsNotSupported = (
    <SqCallout className="display-flex align-items-center">
      <i className="fa-regular fa-diamond-exclamation gold mr-3" style={{ alignContent: 'center' }} />
      <span className="text-bold small">{t('videoNotSupported')}</span>
    </SqCallout>
  )

  const sendVideoToWorkerConversionTag =
    // @ts-ignore
    conversionStatus === 'conversionError' ? (
      tagConversionError
    ) : (
      <SqCallout backgroundColor="var(--color_bg_box_system_warning)" className="py-1 px-3">
        <div className="display-flex justify-content-space-between align-items-center" style={{ width: '100%', gap: '5px' }}>
          <div className="small">
            {conversionStatus === 'converting' || loadingConversion ? t('mediaInConversionMessage') : t('sendVideoToWorkerConversion')}
          </div>
          <SqButton
            className="button-conversion display-flex justify-content-center align-items-center"
            size="sm"
            style={{ minWidth: '30px', minHeight: '30px' }}
            onClick={handleSendToConversion}
            disabled={loadingConversion || conversionStatus === 'converting'}
          >
            {loadingConversion || conversionStatus === 'converting' ? (
              <SqLoader size="small" />
            ) : width < 768 ? (
              <i className="icon-conversion fa-regular fa-video position-absolute" />
            ) : (
              t('buttonLabelSendVideoToWorkerConversion')
            )}
          </SqButton>
        </div>
      </SqCallout>
    )

  const contentFallback =
    media?.medias?.length > 1 ? (
      <SqCarousel
        showPagination
        slides={media?.medias?.map(({ link, type, conversionStatus }, index) => {
          if (type === 'video' && (['notNeedConversion', 'converted', 'unknownCodec'].includes(conversionStatus) || !conversionStatus)) {
            return (
              <div key={`video-${index}`}>
                <video controls>
                  <source src={link} />
                  {videoIsNotSupported}
                </video>
                {!['converted']?.includes(conversionStatus) ? sendVideoToWorkerConversionTag : null}
              </div>
            )
          } else if ((type === 'video' && ['toConvert', 'converting'].includes(conversionStatus)) || !conversionStatus) {
            return (
              <div key={`video-${index}`}>
                <video controls>
                  <source src={link} />
                  {videoIsNotSupported}
                </video>
                {!['converted']?.includes(conversionStatus) ? sendVideoToWorkerConversionTag : null}
              </div>
            )
          }
          return link && <img src={link} alt={media?.caption} key={index} />
        })}
      />
    ) : media?.medias?.[0]?.type === 'video' &&
      (['converted', 'notNeedConversion', 'unknownCodec'].includes(conversionStatus) || !conversionStatus) ? (
      <div>
        <video controls>
          <source src={media?.medias?.[0]?.link} />
          {videoIsNotSupported}
        </video>
        {!['converted']?.includes(conversionStatus) ? sendVideoToWorkerConversionTag : null}
      </div>
    ) : media?.medias?.[0]?.type === 'video' && ['conversionError'].includes(conversionStatus) ? (
      <div>
        <video controls>
          <source src={media?.medias?.[0]?.link} />
          {videoIsNotSupported}
        </video>
        {!['converted']?.includes(conversionStatus) ? sendVideoToWorkerConversionTag : null}
      </div>
    ) : media?.medias?.[0]?.type === 'video' ? (
      <div>
        <video controls>
          <source src={media?.medias?.[0]?.link} />
          {videoIsNotSupported}
        </video>
        {!['converted']?.includes(conversionStatus) ? sendVideoToWorkerConversionTag : null}
      </div>
    ) : media?.medias?.[0]?.type === 'video' && ['conversionError'].includes(conversionStatus) ? (
      tagConversionError
    ) : (
      <img
        src={media?.medias?.[0]?.link}
        alt={media?.caption}
        title={media?.caption}
        onError={({ currentTarget }) => {
          currentTarget.onerror = null
          currentTarget.src = defaultImage
        }}
      />
    )

  if (loading) {
    return <SqLoader size="big" />
  }

  return (
    <div className={`content-approval-card ${className}`} style={style}>
      <div className="card-header">
        <div className="tag-user">
          <img
            src={identity?.picture}
            alt={identity?.username}
            className="profile-image"
            onError={({ currentTarget }) => {
              currentTarget.onerror = null
              currentTarget.src = defaultImage
            }}
          />
          <span className="profile-name">{identity?.name || identity?.username}</span>
        </div>
        {canEdit && (
          <SqButton color="transparent" onClick={() => onEditMedia()} customPadding="0">
            <i className="button-edit fa-solid fa-pen-to-square" />
          </SqButton>
        )}
      </div>
      {canEdit && (
        <div className="content-reproved">
          {media?.status === 'finalReproved' && (
            <div>
              <h5 className="m-0">
                <i className="mr-2 fa-solid fa-diamond-exclamation" style={{ color: 'var(--gold)' }} />
                {t('editPost')}
              </h5>
              <p>
                {t('postNotes')}
                <ul className="m-0">
                  <li>{media?.final?.comment}</li>
                </ul>
              </p>
            </div>
          )}
          <SqButton
            key="editPostButton"
            color="var(--background_secondary)"
            textColor="var(--primary_color)"
            className="button-edit"
            style={{ border: '1px solid var(--primary_color)', fontWeight: 400 }}
            onClick={onEditMedia}
          >
            <i className="mr-2 fa-solid fa-pen-to-square" />
            {t('buttonLabelEditPost')}
          </SqButton>
        </div>
      )}
      <div className="infos-content-tag">
        <div className="tag-status">
          <Tag
            icon={<i className={`${factory?.status[media?.status].icon}`} style={{ color: factory?.status[media?.status].color }} />}
            key="status-tag"
            labels={[
              <span style={{ fontSize: '0.8rem' }} key="status">
                {t('status')}
              </span>,
              <span className="text-bold" style={{ fontSize: '0.8rem' }} key="status-text">
                {factory?.status[media?.status].text}
              </span>,
              <SqTip
                message={`${t(`tooltip.${media?.status}`)} ${
                  media?.status === 'finalReproved' ? `"${t('edit')} ${t(`typesPublications.${media?.type}`)}"` : ''
                } `}
                icon={'fa-light fa-circle-info'}
              />,
            ]}
            style={{ width: '100%' }}
          />
        </div>
        <div className="infos-content">
          <Tag
            icon={<i className={`${iconsEnum.startDate}`} />}
            key="start-date-tag"
            labels={[
              <span style={{ fontSize: '0.8rem' }}>{t('sentIn')}</span>,
              <span className="text-bold" style={{ fontSize: '0.8rem' }}>
                {new Date(media?.createdAt).toLocaleDateString(i18n.language)}
              </span>,
            ]}
            separator
          />
          <Tag
            icon={<i className="fa-light fa-image-polaroid" />}
            key="type-tag"
            labels={[
              <span style={{ fontSize: '0.8rem' }}>{t('type')}</span>,
              <span className="text-bold" style={{ fontSize: '0.8rem' }}>
                {t(`typesPublications.${media?.type}`)}
              </span>,
            ]}
            separator
          />
        </div>
      </div>
      <div className="post-content">
        {media?.medias && ['post', 'reels', 'story'].includes(media?.type) ? (
          <div className="post-content">{contentFallback}</div>
        ) : media?.medias?.length > 1 ? (
          <SqCarousel
            showPagination
            slides={media?.medias?.map(({ link, type, conversionStatus }, index) => {
              if (
                type === 'video' &&
                (['notNeedConversion', 'converted', 'unknownCodec'].includes(conversionStatus) || !conversionStatus)
              ) {
                return (
                  <div key={`video-${index}`}>
                    <video controls>
                      <source src={link} />
                      {videoIsNotSupported}
                    </video>
                  </div>
                )
              } else if (type === 'video' || !conversionStatus) {
                return (
                  <div key={`video-${index}`}>
                    <video controls>
                      <source src={link} />
                      {videoIsNotSupported}
                    </video>
                    {!['converted']?.includes(conversionStatus) ? sendVideoToWorkerConversionTag : null}
                  </div>
                )
              }
              return link && <img src={link} alt={media?.caption} key={index} />
            })}
          />
        ) : (
          media?.medias?.map(({ link, type }, index) => {
            if (type === 'video') {
              return (
                <div key={index}>
                  <video controls>
                    <source src={link} />
                    {videoIsNotSupported}
                  </video>
                </div>
              )
            }
            return link && <img src={link} alt={media?.caption} key={index} />
          })
        )}
      </div>
      <div className="post-description">
        <p className="description">
          <span className="username text-bold">@{identity?.username}</span>
          {` ${media?.caption}`}
        </p>
        {!!media?.tags && (
          <p className="tags my-2">
            {media?.tags?.map((tag) => (
              <span className="tag">{`#${tag}`}</span>
            ))}
          </p>
        )}
      </div>
    </div>
  )
}
